"use client";

import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { settingsApi, type PlatformSettings } from "@/lib/api/endpoints";

interface SettingsContextValue {
  settings: PlatformSettings;
  isLeaderboardEnabled: boolean;
  isFreePlanEnabled: boolean;
  /** Admin "Cloud execution counts": when off, run quotas are shown as "Unlimited". */
  isExecutionCountVisible: boolean;
  isCoursesEnabled: boolean;
  isLoading: boolean;
  reloadSettings: () => Promise<void>;
}

const defaultSettings: PlatformSettings = {
  leaderboard_enabled: true,
  free_plan_enabled: true,
  cloud_execution_counts_enabled: true,
  courses_enabled: true,
};

const SETTINGS_CACHE_KEY = "targetqa:platform:settings_cache";
const SETTINGS_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

interface CachedSettingsEnvelope {
  data: PlatformSettings;
  timestamp: number;
}

function readCachedSettings(): PlatformSettings | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(SETTINGS_CACHE_KEY);
    if (!raw) return null;
    const envelope = JSON.parse(raw) as CachedSettingsEnvelope;
    if (envelope && typeof envelope === "object" && envelope.data) {
      return envelope.data;
    }
  } catch {
    // Ignore storage parse error
  }
  return null;
}

function isCacheExpired(): boolean {
  if (typeof window === "undefined") return true;
  try {
    const raw = window.localStorage.getItem(SETTINGS_CACHE_KEY);
    if (!raw) return true;
    const envelope = JSON.parse(raw) as CachedSettingsEnvelope;
    return !envelope.timestamp || Date.now() - envelope.timestamp > SETTINGS_CACHE_TTL_MS;
  } catch {
    return true;
  }
}

function writeCachedSettings(data: PlatformSettings): void {
  if (typeof window === "undefined") return;
  try {
    const envelope: CachedSettingsEnvelope = {
      data,
      timestamp: Date.now(),
    };
    window.localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify(envelope));
  } catch {
    // Ignore storage write error
  }
}

const SettingsContext = createContext<SettingsContextValue>({
  settings: defaultSettings,
  isLeaderboardEnabled: true,
  isFreePlanEnabled: true,
  isExecutionCountVisible: true,
  isCoursesEnabled: true,
  isLoading: false,
  reloadSettings: async () => {},
});

export function SettingsProvider({
  children,
  initialSettings,
}: {
  children: React.ReactNode;
  initialSettings?: PlatformSettings;
}) {
  const [settings, setSettings] = useState<PlatformSettings>(initialSettings ?? defaultSettings);
  const [isLoading, setIsLoading] = useState<boolean>(!initialSettings);

  const reloadSettings = useCallback(async () => {
    try {
      const data = await settingsApi.get();
      if (data && typeof data === "object") {
        setSettings((prev) => ({ ...prev, ...data }));
        writeCachedSettings(data);
      }
    } catch {
      // Keep sensible defaults on network failure
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (initialSettings) return;

    // 1. Immediately apply cached settings from client storage to prevent UI layout shift
    const cached = readCachedSettings();
    if (cached) {
      setSettings((prev) => ({ ...prev, ...cached }));
      setIsLoading(false);
    }

    // 2. Fetch fresh settings in background if no cache or expired
    if (isCacheExpired() || !cached) {
      let active = true;
      settingsApi
        .get()
        .then((data) => {
          if (active && data && typeof data === "object") {
            setSettings((prev) => ({ ...prev, ...data }));
            writeCachedSettings(data);
          }
        })
        .catch(() => {
          // Keep cached or default settings on network failure
        })
        .finally(() => {
          if (active) {
            setIsLoading(false);
          }
        });

      return () => {
        active = false;
      };
    }
  }, [initialSettings]);

  const isLeaderboardEnabled = useMemo(() => {
    // If explicitly false, it is disabled; otherwise defaults to true
    return settings.leaderboard_enabled !== false;
  }, [settings.leaderboard_enabled]);

  const isFreePlanEnabled = useMemo(() => {
    // Free plan execution tier defaults to true unless explicitly disabled
    return settings.free_plan_enabled !== false;
  }, [settings.free_plan_enabled]);

  const isExecutionCountVisible = useMemo(() => {
    // Counts are shown unless an admin explicitly turns them off
    return settings.cloud_execution_counts_enabled !== false;
  }, [settings.cloud_execution_counts_enabled]);

  const isCoursesEnabled = useMemo(() => {
    return settings.courses_enabled !== false;
  }, [settings.courses_enabled]);

  const value = useMemo(
    () => ({
      settings,
      isLeaderboardEnabled,
      isFreePlanEnabled,
      isExecutionCountVisible,
      isCoursesEnabled,
      isLoading,
      reloadSettings,
    }),
    [settings, isLeaderboardEnabled, isFreePlanEnabled, isExecutionCountVisible, isCoursesEnabled, isLoading, reloadSettings],
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function usePlatformSettings(): SettingsContextValue {
  return useContext(SettingsContext);
}
